import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { form, required } from '@angular/forms/signals';
import { QuoteService } from '../../quote.service';
import { QuoteRevision } from '../../../models/quote-revision.model';
import { Drawer } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { PriceSettingComponent } from '../../shared/price-setting/price-setting.component';
import { ConfirmationService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { Subscription } from 'rxjs';

interface ShippingPackagingFormModel {
  shippingCost: number;
  shippingMarkup: number;
  shippingMarkupPercent: number;
  shippingPrice: number;
  packagingCost: number;
  packagingMarkup: number;
  packagingMarkupPercent: number;
  packagingPrice: number;
}

@Component({
  selector: 'app-shipping-packaging-edit',
  imports: [
    Drawer,
    ButtonModule,
    Divider,
    PriceSettingComponent,
    ConfirmPopupModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './shipping-packaging-edit.component.html',
  styleUrl: './shipping-packaging-edit.component.scss',
})
export class ShippingPackagingEditComponent implements OnInit, OnDestroy {
  quoteService = inject(QuoteService);
  confirmationService = inject(ConfirmationService);

  visible: boolean = false;
  isSaving = signal(false);

  revision: QuoteRevision | null = null;
  private subscriptions = new Subscription();

  formModel = signal<ShippingPackagingFormModel>({
    shippingCost: 0,
    shippingMarkup: 0,
    shippingMarkupPercent: 0,
    shippingPrice: 0,
    packagingCost: 0,
    packagingMarkup: 0,
    packagingMarkupPercent: 0,
    packagingPrice: 0,
  });

  editForm = form(this.formModel, (p) => {
    required(p.shippingCost);
    required(p.shippingMarkup);
    required(p.shippingMarkupPercent);
    required(p.shippingPrice);
    required(p.packagingCost);
    required(p.packagingMarkup);
    required(p.packagingMarkupPercent);
    required(p.packagingPrice);
  });

  ngOnInit() {
    this.subscriptions.add(
      this.quoteService.revision$.subscribe((revision) => {
        if (revision) {
          this.revision = revision;
          this.loadValues();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  open() {
    this.visible = true;
    this.loadValues();
  }

  confirmClose(event: Event) {
    if (!this.editForm().dirty()) {
      this.visible = false;
      return;
    }
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message:
        'You have unsaved changes. Are you sure you want to close without saving?',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonProps: {
        severity: 'danger',
      },
      accept: () => {
        this.visible = false;
      },
      rejectButtonProps: {
        severity: 'secondary',
        outlined: true,
      },
    });
  }

  private loadValues() {
    if (!this.revision) return;

    const shippingCost = Number.parseFloat(this.revision.shipping_cost);
    const shippingMarkup = Number.parseFloat(this.revision.shipping_markup);
    const shippingMarkupPercent =
      Math.round(
        (shippingCost !== 0 ? (shippingMarkup / shippingCost) * 100 : 0) * 100,
      ) / 100;
    const shippingPrice = Number.parseFloat(this.revision.shipping_price);

    const packagingCost = Number.parseFloat(this.revision.packaging_cost);
    const packagingMarkup = Number.parseFloat(this.revision.packaging_markup);
    const packagingMarkupPercent =
      Math.round(
        (packagingCost !== 0 ? (packagingMarkup / packagingCost) * 100 : 0) *
          100,
      ) / 100;
    const packagingPrice = Number.parseFloat(this.revision.packaging_price);

    this.formModel.set({
      shippingCost,
      shippingMarkup,
      shippingMarkupPercent,
      shippingPrice,
      packagingCost,
      packagingMarkup,
      packagingMarkupPercent,
      packagingPrice,
    });

    this.editForm().reset();
  }

  async saveChanges() {
    if (!this.revision) return;

    this.isSaving.set(true);
    const patch: Partial<QuoteRevision> = {};

    const shippingDirty =
      this.editForm.shippingCost().dirty() ||
      this.editForm.shippingMarkup().dirty() ||
      this.editForm.shippingMarkupPercent().dirty() ||
      this.editForm.shippingPrice().dirty();

    if (shippingDirty) {
      patch.shipping_cost = this.editForm.shippingCost().value().toFixed(2);
      patch.shipping_markup = this.editForm.shippingMarkup().value().toFixed(2);
    }

    const packagingDirty =
      this.editForm.packagingCost().dirty() ||
      this.editForm.packagingMarkup().dirty() ||
      this.editForm.packagingMarkupPercent().dirty() ||
      this.editForm.packagingPrice().dirty();

    if (packagingDirty) {
      patch.packaging_cost = this.editForm.packagingCost().value().toFixed(2);
      patch.packaging_markup = this.editForm
        .packagingMarkup()
        .value()
        .toFixed(2);
    }

    try {
      await this.quoteService.patchRevision(patch);
      this.loadValues();
      this.visible = false;
    } catch (error) {
      console.error('Error saving shipping/packaging changes:', error);
    } finally {
      this.isSaving.set(false);
    }
  }
}
